import React, { useCallback, useEffect, useRef, useState } from "react";
import { LogOut, MessageCircle, User } from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { getUnreadCount } from "@/api/agent";
import { useQuestionsForMe } from "@/components/agent/PendingQuestions";
import AssistantPanel from "@/components/assistant/AssistantPanel";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const NAV_LINK_CLASS =
  "rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground";

const OPEN_KEY = "autobio_assistant_open";
const CONTEXT_KEY = "autobio_assistant_context";

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (_) {
    /* ignoré */
  }
};

export default function AppShell({ user, isTeam, onLogout, token }) {
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [assistantOpen, setAssistantOpen] = useState(() => read(OPEN_KEY) === "1");
  const [assistantContext, setAssistantContext] = useState(() => read(CONTEXT_KEY) || null);
  const [pendingQuestions] = useQuestionsForMe();
  const assistantButtonRef = useRef(null);
  const location = useLocation();
  const clientsRouteIsActive = location.pathname.startsWith("/clients");

  const setOpen = useCallback((value) => {
    setAssistantOpen(value);
    write(OPEN_KEY, value ? "1" : "0");
  }, []);

  const setContext = useCallback((name) => {
    setAssistantContext(name);
    write(CONTEXT_KEY, name);
  }, []);

  const openAssistant = useCallback((name) => {
    setContext(name);
    setOpen(true);
  }, [setContext, setOpen]);

  const refreshUnread = useCallback(async () => {
    try {
      const data = await getUnreadCount();
      setUnreadTotal(data?.total || 0);
    } catch (_) {
      /* silencieux */
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    const timer = setInterval(refreshUnread, 30000);
    return () => {
      clearInterval(timer);
    };
  }, [refreshUnread]);

  return (
    <>
      <header
        style={{ height: "var(--shell-h)" }}
        className="sticky top-0 z-40 border-b bg-card"
      >
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <Link to="/" className="shrink-0 font-heading text-xl font-semibold text-foreground">
              AutoBio
            </Link>
            <nav className="flex items-center gap-1" aria-label="Navigation principale">
              <NavLink
                end
                to="/"
                className={({ isActive }) => cn(
                  NAV_LINK_CLASS,
                  (isActive || clientsRouteIsActive) && "bg-muted text-foreground"
                )}
              >
                Clients
              </NavLink>
              {isTeam && (
                <NavLink
                  to="/nouveautes"
                  className={({ isActive }) => cn(
                    NAV_LINK_CLASS,
                    isActive && "bg-muted text-foreground"
                  )}
                >
                  Nouveautés
                </NavLink>
              )}
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              ref={assistantButtonRef}
              variant="outline"
              className="relative"
              aria-expanded={assistantOpen}
              aria-controls="assistant-panel"
              onClick={() => setOpen(!assistantOpen)}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Assistant</span>
              {unreadTotal > 0 && (
                <span
                  aria-label={`${unreadTotal} messages non lus`}
                  className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-semibold text-white"
                >
                  {unreadTotal > 9 ? "9+" : unreadTotal}
                </span>
              )}
            </Button>

            {pendingQuestions.length > 0 && (
              <Button
                variant="outline"
                asChild
                className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
              >
                <Link to="/mes-demandes" title="Des questions attendent ta réponse">
                  ❓ {pendingQuestions.length} question
                  {pendingQuestions.length > 1 ? "s" : ""}
                </Link>
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="max-w-48">
                  <User className="h-4 w-4" />
                  <span className="hidden truncate sm:inline">
                    {user?.name || user?.email || "Compte"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onLogout}>
                  <LogOut className="h-4 w-4" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className={cn(assistantOpen && "lg:pr-[440px]")}>
        <Outlet context={{ openAssistant }} />
      </main>
      <AssistantPanel
        token={token}
        open={assistantOpen}
        onOpenChange={setOpen}
        contextName={assistantContext}
        onContextChange={setContext}
        onConversationOpened={refreshUnread}
        returnFocusTo={assistantButtonRef}
      />
      <Toaster position="top-right" richColors />
    </>
  );
}
